/**
 * Degrees of Freedom Arrow Components
 * Renders translational and rotational arrows for support visualization
 * Z-UP Coordinate System: X=East-West, Y=North-South, Z=Up-Down
 */

import React from 'react';
import * as THREE from 'three';

interface DOFArrowProps {
  position: THREE.Vector3;
  direction: THREE.Vector3;
  color: string;
  length: number;
  visible: boolean;
  isSpring?: boolean;
}

/**
 * Translational DOF Arrow - Straight arrow from center passing through support
 */
export const TranslationalArrow: React.FC<DOFArrowProps> = ({
  position,
  direction,
  color: _color, // Unused - using black color for visibility
  length: _length, // Unused - using fixed values
  visible,
  isSpring = false
}) => {
  if (!visible) return null;

  // Regular arrow head geometry for straight arrows
  const arrowHeadGeometry = React.useMemo(() => {
    return new THREE.ConeGeometry(0.08, 0.25, 8); // Smaller size for inside cube
  }, []);

  // Separate arrow head geometry for spring arrows - same properties but separate instance
  const springArrowHeadGeometry = React.useMemo(() => {
    if (!isSpring) return null;
    return new THREE.ConeGeometry(0.08, 0.25, 8); // Same size as regular arrows
  }, [isSpring]);

  const shaftGeometry = React.useMemo(() => {
    const shaftLength = 0.65; // Reduced length to fit comfortably inside cube
    return new THREE.CylinderGeometry(0.015, 0.015, shaftLength);
  }, []);

  // Spring coil geometry for spring arrows
  const springGeometry = React.useMemo(() => {
    if (!isSpring) return null;
    
    const springLength = 0.9; // Fixed length for better visibility
    const coilRadius = 0.07;
    const coilTurns = 7;
    const pointsPerTurn = 8;
    const totalPoints = coilTurns * pointsPerTurn;
    
    const points: THREE.Vector3[] = [];
    for (let i = 0; i <= totalPoints; i++) {
      const t = i / totalPoints;
      const y = (t - 0.5) * springLength;
      const angle = t * coilTurns * Math.PI * 2;
      const x = Math.cos(angle) * coilRadius;
      const z = Math.sin(angle) * coilRadius;
      points.push(new THREE.Vector3(x, y, z));
    }
    
    const curve = new THREE.CatmullRomCurve3(points);
    return new THREE.TubeGeometry(curve, totalPoints, 0.016, 6, false);
  }, [isSpring]);

  const material = React.useMemo(() => {
    return new THREE.MeshBasicMaterial({ 
      color: '#ff0000', // Red color for translational arrows
      transparent: false,
      opacity: 1.0
    });
  }, []);



  // Calculate arrow orientation - align with direction
  const quaternion = React.useMemo(() => {
    const quaternion = new THREE.Quaternion();
    const up = new THREE.Vector3(0, 1, 0);
    quaternion.setFromUnitVectors(up, direction.clone().normalize());
    return quaternion;
  }, [direction]);

  // Position arrow heads at both ends
  const headDistance = isSpring ? 0.575 : 0.45; // 0.9/2 + 0.125 = 0.575 for springs
  const arrowHead1Position = position.clone().add(
    direction.clone().normalize().multiplyScalar(headDistance)
  );
  
  const arrowHead2Position = position.clone().add(
    direction.clone().normalize().multiplyScalar(-headDistance)
  );
  
  // Position shaft at center
  const shaftPosition = position.clone();
  
  // Rotation for second arrow head (pointing opposite direction)
  const oppositeQuaternion = React.useMemo(() => {
    const quaternion = new THREE.Quaternion();
    const up = new THREE.Vector3(0, 1, 0);
    quaternion.setFromUnitVectors(up, direction.clone().normalize().negate());
    return quaternion;
  }, [direction]);

  return (
    <group>
      {!isSpring ? (
        <>
          {/* Regular arrow shaft */}
          <mesh
            position={shaftPosition}
            quaternion={quaternion}
            geometry={shaftGeometry}
            material={material}
          />
          
          {/* Arrow head 1 */}
          <mesh
            position={arrowHead1Position}
            quaternion={quaternion}
            geometry={arrowHeadGeometry}
            material={material}
          />
          
          {/* Arrow head 2 */}
          <mesh
            position={arrowHead2Position}
            quaternion={oppositeQuaternion}
            geometry={arrowHeadGeometry}
            material={material}
          />
        </>
      ) : (
        <>
          {/* Spring coil */}
          {springGeometry && (
            <mesh
              position={position}
              quaternion={quaternion}
              geometry={springGeometry}
              material={material}
            />
          )}
          
          {/* Spring arrow head 1 */}
          {springArrowHeadGeometry && (
            <mesh
              position={arrowHead1Position}
              quaternion={quaternion}
              geometry={springArrowHeadGeometry}
              material={material}
            />
          )}
          
          {/* Spring arrow head 2 */}
          {springArrowHeadGeometry && (
            <mesh
              position={arrowHead2Position}
              quaternion={oppositeQuaternion}
              geometry={springArrowHeadGeometry}
              material={material}
            />
          )}
        </>
      )}
    </group>
  );
};

interface RotationalArrowProps {
  position: THREE.Vector3;
  axis: 'x' | 'y' | 'z';
  radius: number;
  visible: boolean;
}

/**
 * Rotational DOF Arrow - Circular arrow around axis, INSIDE support cube
 */
export const RotationalArrow: React.FC<RotationalArrowProps> = ({
  position,
  axis,
  radius, // Now using the passed radius value
  visible
}) => {
  if (!visible) return null;

  // Create semicircular arc geometry with passed radius (now fits inside cube)
  const curveGeometry = React.useMemo(() => {
    const axisRadius = radius; // Use the passed radius value
    
    const curve = new THREE.EllipseCurve(
      0, 0,                  // center
      axisRadius, axisRadius, // axis-specific radius
      0, Math.PI,            // semicircle: start at 0, end at PI
      false,                 // clockwise
      0                      // rotation
    );

    const points = curve.getPoints(32); // Smooth curve with more points
    const curve3D = new THREE.CatmullRomCurve3(
      points.map(p => new THREE.Vector3(p.x, p.y, 0))
    );
    
    return new THREE.TubeGeometry(curve3D, 32, 0.02, 8, false); // Thinner tube for inside cube
  }, [axis]);

  const arrowHeadGeometry = React.useMemo(() => {
    return new THREE.ConeGeometry(0.08, 0.25, 8); // Smaller size for inside cube
  }, []);

  const solidMaterial = React.useMemo(() => {
    return new THREE.MeshBasicMaterial({ 
      color: '#000000', // Always black for rotational arrows
      transparent: false,
      side: THREE.DoubleSide
    });
  }, []);



  // Calculate rotation for each axis plane with proper Z-UP global axis alignment
  const { rotation, positionOffset } = React.useMemo(() => {
    const euler = new THREE.Euler();
    let offset = new THREE.Vector3(0, 0, 0);
    
    switch (axis) {
      case 'x': // Rotation around X-axis (YZ plane) - East-West axis
        euler.set(0, Math.PI / 2, Math.PI); // Rotate to YZ plane and flip to lower side
        offset.set(0.3, 0, 0); // Slight offset along X-axis (East)
        break;
      case 'y': // Rotation around Y-axis (XZ plane) - North-South axis
        euler.set(Math.PI / 2, 0, 0); // Rotate to align with XZ plane
        offset.set(0, 0.3, 0); // Slight offset along Y-axis (North)
        break;
      case 'z': // Rotation around Z-axis (XY plane) - Vertical axis (UP-DOWN)
        euler.set(0, 0, 0); // Default XY plane orientation (horizontal)
        offset.set(0, 0, 0.3); // Slight offset along Z-axis (Up)
        break;
    }
    
    return { rotation: euler, positionOffset: offset };
  }, [axis]);

  const { arrowHead1Position, arrowHead2Position } = React.useMemo(() => {
    // Use the actual radius passed to the component (same as curve radius)
    const actualRadius = radius;
    
    // Position arrow heads exactly at the semicircle endpoints
    const pos1 = new THREE.Vector3();
    const pos2 = new THREE.Vector3();
    
    // For a semicircle from 0 to PI:
    // At angle 0: (radius, 0, 0) - right end
    // At angle PI: (-radius, 0, 0) - left end
    
    pos1.set(-actualRadius, 0, 0); // Left end of semicircle (angle PI)
    pos2.set(actualRadius, 0, 0);  // Right end of semicircle (angle 0)
    
    return {
      arrowHead1Position: pos1,
      arrowHead2Position: pos2
    };
  }, [radius]);

  const { arrowHead1Rotation, arrowHead2Rotation } = React.useMemo(() => {
    const euler1 = new THREE.Euler();
    const euler2 = new THREE.Euler();
    
    // Arrow heads tilted more inwards for better visual indication
    // At angle PI (left end): tilt inward toward center
    // At angle 0 (right end): tilt inward toward center

    // Tilt arrow heads inward by 180 degrees from tangent direction
    euler1.set(0, 0, Math.PI);   // Left arrow head tilted inward (180°)
    euler2.set(0, 0, -Math.PI);  // Right arrow head tilted inward (-180°)    
    return {
      arrowHead1Rotation: euler1,
      arrowHead2Rotation: euler2
    };
  }, [axis]);

  return (
    <group position={position.clone().add(positionOffset)} rotation={rotation}>
      {/* Semicircular arc as tube - no outline */}
      <mesh geometry={curveGeometry} material={solidMaterial} />
      
      {/* Arrow head 1 - properly aligned with shaft */}
      <mesh
        position={arrowHead1Position}
        rotation={arrowHead1Rotation}
        geometry={arrowHeadGeometry}
        material={solidMaterial}
      />
      
      {/* Arrow head 2 - properly aligned with shaft */}
      <mesh
        position={arrowHead2Position}
        rotation={arrowHead2Rotation}
        geometry={arrowHeadGeometry}
        material={solidMaterial}
      />
    </group>
  );
};

interface DOFIndicatorProps {
  position: THREE.Vector3;
  dof: {
    Fx: number; Fy: number; Fz: number;
    Rx: number; Ry: number; Rz: number;
  };
  supportSize: number;
  supportColor: string; // Color from the support type
}

/**
 * Dashed Line Component - Shows dashed lines when F xyz = 0 but R xyz arrows are applied
 */
const DashedLine: React.FC<{
  position: THREE.Vector3;
  direction: THREE.Vector3;
  length: number;
  color: string;
}> = ({ position, direction, length, color }) => {
  const dashedGeometry = React.useMemo(() => {
    const points = [
      position.clone().add(direction.clone().normalize().multiplyScalar(-length / 2)),
      position.clone().add(direction.clone().normalize().multiplyScalar(length / 2))
    ];
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    // Create line distances manually for dashed material
    const distances = new Float32Array([0, points[0].distanceTo(points[1])]);
    geometry.setAttribute('lineDistance', new THREE.BufferAttribute(distances, 1));
    return geometry;
  }, [position, direction, length]);

  const dashedMaterial = React.useMemo(() => {
    return new THREE.LineDashedMaterial({
      color: color,
      linewidth: 2,
      scale: 1,
      dashSize: 0.1,
      gapSize: 0.05,
    });
  }, [color]);

  return <lineSegments geometry={dashedGeometry} material={dashedMaterial} />;
};

/**
 * Complete DOF Indicator - Shows all 6 DOF arrows around a support
 * Z-UP System: X=East-West, Y=North-South, Z=Up-Down
 * 
 * 0 = fixed/constrained (no arrow)
 * 1 = free (colored arrow)
 * other values = spring (orange spring arrow)
 * 
 * REQUIREMENTS:
 * - Translational arrows: Fx (X/East-West), Fy (Y/North-South), Fz (Z/Up-Down)
 * - Rotational arrows: Rx (around X), Ry (around Y), Rz (around Z-vertical)
 * - Rotational arrows must be INSIDE the fixed support cube
 * - Straight arrows pass through center from rotational circular arrows
 * - If F xyz = 0, show dashed line of same length if R xyz arrows are applied
 */
export const DOFIndicator: React.FC<DOFIndicatorProps> = ({
  position,
  dof,
  supportSize
}) => {
  const arrowLength = supportSize * 1.2; // Scale with support size
  
  // Rotational arrows INSIDE the cube - smaller radius to fit within cube bounds
  const cubeHalfSize = supportSize / 2;
  const rotationRadiusX = cubeHalfSize * 0.5; // Inside cube bounds - around X-axis (East-West)
  const rotationRadiusY = cubeHalfSize * 0.6; // Reduced by 10% more to prevent collisions - around Y-axis (North-South)
  const rotationRadiusZ = cubeHalfSize * 0.8; // Largest for Z - around Z-axis (vertical)

  // Helper to determine if DOF should show arrow and what type
  const getDOFProps = (dofValue: number, isRotational: boolean = false) => {
    if (dofValue === 0) return { show: false, isSpring: false, color: isRotational ? '#000000' : '#ff0000' };
    if (dofValue === 1) return { show: true, isSpring: false, color: isRotational ? '#000000' : '#ff0000' };
    return { show: true, isSpring: true, color: isRotational ? '#000000' : '#ff8c00' }; // Black for rotational, red for translational, orange for springs
  };

  const fxProps = getDOFProps(dof.Fx, false); // X-direction (East-West)
  const fyProps = getDOFProps(dof.Fy, false); // Y-direction (North-South)
  const fzProps = getDOFProps(dof.Fz, false); // Z-direction (Up-Down)
  const rxProps = getDOFProps(dof.Rx, true);  // Rotation around X-axis
  const ryProps = getDOFProps(dof.Ry, true);  // Rotation around Y-axis
  const rzProps = getDOFProps(dof.Rz, true);  // Rotation around Z-axis (vertical)

  // Check if we need dashed lines (F = 0 but R != 0)
  const needDashedX = !fxProps.show && rxProps.show;
  const needDashedY = !fyProps.show && ryProps.show;
  const needDashedZ = !fzProps.show && rzProps.show;

  return (
    <group position={position}>
      {/* Translational arrows - straight through center, Z-UP aligned */}
      {fxProps.show && (
        <TranslationalArrow
          position={new THREE.Vector3(0, 0, 0)}
          direction={new THREE.Vector3(1, 0, 0)} // X-axis: East-West
          color={fxProps.color}
          length={arrowLength}
          visible={true}
          isSpring={fxProps.isSpring}
        />
      )}
      
      {fyProps.show && (
        <TranslationalArrow
          position={new THREE.Vector3(0, 0, 0)}
          direction={new THREE.Vector3(0, 1, 0)} // Y-axis: North-South
          color={fyProps.color}
          length={arrowLength}
          visible={true}
          isSpring={fyProps.isSpring}
        />
      )}
      
      {fzProps.show && (
        <TranslationalArrow
          position={new THREE.Vector3(0, 0, 0)}
          direction={new THREE.Vector3(0, 0, 1)} // Z-axis: Up-Down (vertical)
          color={fzProps.color}
          length={arrowLength}
          visible={true}
          isSpring={fzProps.isSpring}
        />
      )}

      {/* Dashed lines when F xyz = 0 but R xyz arrows are applied */}
      {needDashedX && (
        <DashedLine
          position={new THREE.Vector3(0, 0, 0)}
          direction={new THREE.Vector3(1, 0, 0)} // X-axis
          length={arrowLength}
          color="#ff0000"
        />
      )}
      
      {needDashedY && (
        <DashedLine
          position={new THREE.Vector3(0, 0, 0)}
          direction={new THREE.Vector3(0, 1, 0)} // Y-axis
          length={arrowLength}
          color="#ff0000"
        />
      )}
      
      {needDashedZ && (
        <DashedLine
          position={new THREE.Vector3(0, 0, 0)}
          direction={new THREE.Vector3(0, 0, 1)} // Z-axis
          length={arrowLength}
          color="#ff0000"
        />
      )}
      
      {/* Rotational arrows - INSIDE the cube, Z-UP aligned */}
      {rxProps.show && (
        <RotationalArrow
          position={new THREE.Vector3(0, 0, 0)}
          axis="x" // Rotation around X-axis (East-West), visible in YZ plane
          radius={rotationRadiusX}
          visible={true}
        />
      )}
      
      {ryProps.show && (
        <RotationalArrow
          position={new THREE.Vector3(0, 0, 0)}
          axis="y" // Rotation around Y-axis (North-South), visible in XZ plane
          radius={rotationRadiusY}
          visible={true}
        />
      )}
      
      {rzProps.show && (
        <RotationalArrow
          position={new THREE.Vector3(0, 0, 0)}
          axis="z" // Rotation around Z-axis (vertical), visible in XY plane (horizontal)
          radius={rotationRadiusZ}
          visible={true}
        />
      )}
    </group>
  );
};

export default DOFIndicator;